import { jsx as _jsx } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { getApiInstance } from '../../helpers/api.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { AxonAccountContext } from '../axon_account';
import { SiteContext } from '../site';
import { UserLocaleContext } from '../user_locale/user_locale.context';
export const OrgContext = createContext({
    isLoading: true,
    org: null,
    participantStatusMap: new Map(),
});
export const OrgProvider = ({ children }) => {
    const match = useMatch('/site/study/:studyId/*');
    const { account } = useContext(AxonAccountContext);
    const { site } = useContext(SiteContext);
    const { getUserSelectedLocale, isUserLocaleChecked } = useContext(UserLocaleContext);
    const { environment } = useCurrentEnvironment(match?.params?.studyId);
    const userLocale = isUserLocaleChecked ? getUserSelectedLocale() || undefined : undefined;
    const getStudyData = async () => {
        // Note: environment, account, site are guaranteed to be defined by useQuery's enabled flag
        const url = `/v2/account/${account._id}/c_sites/${site._id}/c_study`;
        const { data: response } = await getApiInstance(environment, userLocale).get(url, {
            params: {
                expand: ['c_study', 'dcr__default_dcr_configurations.dcr__dcr_configuration', 'c_locks'],
                include: ['c_subject_status_list'],
            },
        });
        return response || null;
    };
    const { data: org, isLoading: isStudyDataLoading } = useQuery({
        queryKey: ['getStudyData', site?._id, userLocale],
        queryFn: getStudyData,
        enabled: !!environment && !!account && !!site && isUserLocaleChecked,
    });
    const getOrgConfigData = async () => {
        // Note: environment is guaranteed to be defined by useQuery's enabled flag
        try {
            const { data: response } = await getApiInstance(environment, userLocale).get('/v2/orgs', {
                params: {
                    'include[]': 'configuration.public',
                    'limit': 1,
                },
            });
            return response?.data?.[0]?.configuration?.public || null;
        }
        catch (error) {
            console.warn('Failed to fetch org configuration from orgs endpoint:', error);
            return null;
        }
    };
    const { data: orgConfig, isLoading: isOrgConfigLoading } = useQuery({
        queryKey: ['getOrgConfigData', environment?.id, userLocale],
        queryFn: getOrgConfigData,
        enabled: !!environment && isUserLocaleChecked,
    });
    const orgWithConfig = useMemo(() => {
        if (!org) {
            return null;
        }
        return {
            ...org,
            axon__version: orgConfig?.axon__version?.version,
            _publicConfig: orgConfig || undefined, // rest can be used if needed
        };
    }, [org, orgConfig]);
    // the c_status_label is the localized representation of the c_public_user.c_status
    const participantStatusMap = useMemo(() => {
        const map = new Map();
        orgWithConfig?.c_subject_status_list?.forEach((status) => {
            map.set(status.c_status_value, status.c_status_label);
        });
        return map;
    }, [orgWithConfig?.c_subject_status_list]);
    const isLoading = isStudyDataLoading || isOrgConfigLoading;
    const value = useMemo(() => ({
        isLoading,
        org: orgWithConfig,
        participantStatusMap,
    }), [isLoading, orgWithConfig, participantStatusMap]);
    return _jsx(OrgContext.Provider, { value: value, children: children });
};
